import type { AxiosInstance } from 'axios'
import { isErrorStatus } from '@audience-republic/audience-manager-shared/api/utils'
import type { CustomApiError } from '@audience-republic/audience-manager-shared/api/types'
import type { Campaign, CampaignRegistrationStats } from '@/types/resources/campaign'

export const defaultSelectFields = [
  'name',
  'type',
  'notifyDate',
  'startDate',
  'endDate',
  'timeZone',
  'urlSlug',
  'eventOid',
  'event',
  'rewards',
  'summaryStatsSnapshot',
  'presentation',
  'resources',
  'registrations',
  'settings',
  'socialActions',
  'defaults',
  'subscriptionLevel',
  'status',
].join(',')

export type CampaignStatusFilter = 'all' | 'live' | 'completed'

// Status is filtered server-side so pagination and counts cover every campaign,
// not just the loaded page. A campaign with no end date counts as live.
export function campaignStatusClause(status?: string | null): string | null {
  if (status === 'live') return '(endDate IS NULL OR endDate > "NOW()")'
  if (status === 'completed') return 'endDate < "NOW()"'
  return null
}

export default (axios: AxiosInstance) => ({

  async get(promoterOid: number, campaignOid: number, params: any): Promise<Campaign> {
    const { data, status } = await axios.get(`/promoter/${promoterOid}/campaign/${campaignOid}`, {
      params: {
        $select: params,
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Get Campaign error',
        message: `Error getting Campaign from event with oid of ${campaignOid}`,
        status,
      }
      throw apiError
    }
    return data
  },

  /**
   * Fetch Campaigns by Oids
   * @param promoterAccountOids
   * @returns
   */
  async fetchByOids(promoterOid: number, oids: number[]): Promise<Campaign[] | null> {
    if (oids.length === 0) {
      return []
    }
    const { status, data } = await axios.get(`/promoter/${promoterOid}/campaign`, {
      params: {
        $select: defaultSelectFields,
        $top: 'all',
        $orderby: 'oid desc',
        $filter: `(oid=${oids.join(' OR oid=')}) AND type != "opt-in" AND type != "rsvp"`,
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch Campaigns with oids error',
        message: `Error fetching Campaigns with oids ${oids.join(', ')}`,
        status,
      }
      throw apiError
    }
    return data
  },

  async search(
    promoterOid: number,
    {
      top = 10,
      skip = 0,
      searchString = null,
      orderby = 'oid desc',
      select = defaultSelectFields,
      type = 'all',
      status = 'all',
      signal,
    }: { top: number, skip: number, searchString: string | null, select: string, orderby?: string, type?: string, status?: string, signal?: AbortSignal },
  ): Promise<Campaign[]> {
    // TODO use filterBuilder
    // `all` keeps the default data capture listing (excludes opt-in / rsvp),
    // any other value narrows the listing to that single campaign sub-type.
    let filterString = type && type !== 'all'
      ? `type = "${type}"`
      : 'type != "opt-in" AND type != "rsvp"'
    if (searchString) {
      filterString = `${filterString} AND name ILIKE "%${searchString}%"`
    }
    const statusClause = campaignStatusClause(status)
    if (statusClause) {
      filterString = `${filterString} AND ${statusClause}`
    }
    const { data, status: responseStatus }: { data: Campaign[], status: number } = await axios.get(`/promoter/${promoterOid}/campaign`, {
      params: {
        $select: select,
        $top: top,
        $skip: skip,
        $orderby: orderby,
        $filter: filterString,
      },
      signal,
    })
    if (isErrorStatus(responseStatus)) {
      const apiError: CustomApiError = {
        name: 'Fetch All Campaigns error',
        message: `Error fetching all Campaigns`,
        status: responseStatus,
      }
      throw apiError
    }
    return data
  },

  async fetchCampaignForAutomation(promoterOid: number, oid: number): Promise<any> {
    const { data, status } = await axios.get(`/promoter/${promoterOid}/campaign/${oid}`, {
      params: {
        $select: 'name',
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch Campaign for Automation error',
        message: `Error fetching Campaigns for Automation with promoterOid ${promoterOid},\n
                  and campaign oid ${oid}.`,
        status,
      }
      throw apiError
    }

    return data
  },

  async fetchCampaignRegistrationStats(promoterOid: number, oid: number): Promise<CampaignRegistrationStats | null> {
    if (isNaN(oid)) {
      console.error('FETCH_CAMPAIGN_REGISTRATIONS called with NaN campaignOid')
      return null
    }

    const { data } = await axios.get(
      `/promoter/${promoterOid}/campaign-registration-stats-realtime/${oid}`,
    )

    return data
  },

  async fetchCampaignRegistrationsSeries(promoterOid: number, oid: number): Promise<any> {
    if (isNaN(oid)) {
      console.error('FETCH_CAMPAIGN_REGISTRATIONS_SERIES called with NaN campaignOid')
      return null
    }

    const { data } = await axios.get(
      `/promoter/${promoterOid}/campaign/${oid}/registration-stats-time-series-2/${oid}`,
    )

    // Format of data returned: {"oid":3942,"entries":[{"ts":"2025-06-06","total":4,"viral":0,"direct":4}]}
    return data.entries || []
  },

  async fetchCount(
    promoterOid: number,
    {
      filter = null,
      signal,
    }: {
      filter?: string | null
      signal?: AbortSignal
    }): Promise<{ rows: Campaign[], count: number }> {
    const filterString = filter ? `${filter} AND type != "opt-in" AND type != "rsvp"` : `type != "opt-in" AND type != "rsvp"`

    const { status, data } = await axios.get(`/promoter/${promoterOid}/campaign`, {
      params: {
        $select: 'oid',
        $count: true,
        $top: 0,
        $filter: filterString,
      },
      signal,
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch Campaigns error',
        message: `Error fetching Campaigns`,
        status,
      }
      throw apiError
    }
    return data
  },

  /**
   * Fetch Campaigns
   */
  async fetchAll(
    promoterOid: number,
    {
      top = 20,
      skip = 0,
      orderby = 'sysCtime desc',
      filter = null,
    }: {
      top?: number | string | null
      skip?: number | null
      orderby?: string | null
      filter?: string | null
    }): Promise<{ rows: Campaign[], count: number }> {
    if (!top) top = 20
    if (!skip) skip = 0

    const filterString = filter ? `${filter} AND type != "opt-in" AND type != "rsvp"` : `type != "opt-in" AND type != "rsvp"`

    const { status, data } = await axios.get(`/promoter/${promoterOid}/campaign`, {
      params: {
        $select: defaultSelectFields,
        $top: top,
        $skip: skip,
        $orderby: orderby,
        $filter: filterString,
      },
    })
    if (isErrorStatus(status)) {
      const apiError: CustomApiError = {
        name: 'Fetch Campaigns error',
        message: `Error fetching Campaigns`,
        status,
      }
      throw apiError
    }
    return data
  },
})
